import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT
  ? (API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`)
  : "/api";

const NEW_ALLOWED_MODULES = [1, 3, 7, 13, 16, 19, 29];
const MAX_SUPPORTING_DOC_BYTES = 200 * 1024;

async function compressImageToMaxSize(file, maxBytes) {
  const imgUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imgUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0);

    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const outputType = ext === "png" ? "image/png" : "image/jpeg";
    const toBlobWithQuality = (nextQuality) => new Promise((resolve) => {
      canvas.toBlob(resolve, outputType, nextQuality);
    });

    let quality = outputType === "image/png" ? undefined : 0.9;
    let blob = await toBlobWithQuality(quality);

    if (!blob) {
      throw new Error("Failed to process image.");
    }

    // Try reducing quality for JPEG until under target size.
    while (blob.size > maxBytes && outputType === "image/jpeg" && quality > 0.3) {
      quality -= 0.1;
      blob = await toBlobWithQuality(quality);
      if (!blob) {
        throw new Error("Failed to process image.");
      }
    }

    // If still large, reduce dimensions progressively.
    while (blob.size > maxBytes && canvas.width > 600) {
      canvas.width = Math.round(canvas.width * 0.9);
      canvas.height = Math.round(canvas.height * 0.9);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      blob = await toBlobWithQuality(quality);
      if (!blob) {
        throw new Error("Failed to process image.");
      }
    }

    const finalExt = outputType === "image/png" ? "png" : "jpg";
    const baseName = file.name.replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.${finalExt}`, { type: outputType });
  } finally {
    URL.revokeObjectURL(imgUrl);
  }
}

export default function NewResidentialComplaint() {
  const navigate = useNavigate();
  const uploadInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [resiTypes, setResiTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedDocInfo, setSelectedDocInfo] = useState("");

  const [form, setForm] = useState({
    ComplaintCategoryID: "",
    ComplaintID: "",
    ToDepartmentID: "",
    ResiTypeID: "",
    ResidenceID: "",
    Description: "",
    SupportingDoc: null
  });

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const employeeId = Number.parseInt(localUser?.id, 10);
  const moduleId = Number.parseInt(localUser?.moduleId, 10);

  useEffect(() => {
    const loadInitial = async () => {
      if (!employeeId || !moduleId || !NEW_ALLOWED_MODULES.includes(moduleId)) {
        setError("Unauthorized access Or Session Timed out.");
        return;
      }

      try {
        setError("");
        const [catRes, resiRes] = await Promise.all([
          fetch(`${API_BASE}/complaints/categories`),
          fetch(`${API_BASE}/complaints/residence-types`)
        ]);

        const catPayload = await catRes.json();
        const resiPayload = await resiRes.json();

        if (!catRes.ok) throw new Error(catPayload?.message || "Failed to fetch complaint categories");
        if (!resiRes.ok) throw new Error(resiPayload?.message || "Failed to fetch residence types");

        setCategories(Array.isArray(catPayload?.data) ? catPayload.data : []);
        setResiTypes(Array.isArray(resiPayload?.data) ? resiPayload.data : []);
      } catch (err) {
        setError(err.message || "Failed to load complaint form");
      }
    };

    loadInitial();
  }, [employeeId, moduleId]);

  const onCategoryChange = async (value) => {
    setForm((prev) => ({
      ...prev,
      ComplaintCategoryID: value,
      ComplaintID: "",
      ToDepartmentID: ""
    }));

    if (!value) {
      setComplaints([]);
      return;
    }

    try {
      setError("");
      const descQuery = new URLSearchParams({ complaintCategoryId: String(value), viewType: "R" });
      const depQuery = new URLSearchParams({ complaintCategoryId: String(value) });

      const [descRes, depRes] = await Promise.all([
        fetch(`${API_BASE}/complaints/descriptions?${descQuery.toString()}`),
        fetch(`${API_BASE}/complaints/category-department?${depQuery.toString()}`)
      ]);

      const descPayload = await descRes.json();
      const depPayload = await depRes.json();

      if (!descRes.ok) throw new Error(descPayload?.message || "Failed to fetch complaint descriptions");
      if (!depRes.ok) throw new Error(depPayload?.message || "Failed to fetch complaint department");

      setComplaints(Array.isArray(descPayload?.data) ? descPayload.data : []);
      setForm((prev) => ({
        ...prev,
        ToDepartmentID: String(depPayload?.data?.DepartmentID || "")
      }));
    } catch (err) {
      setComplaints([]);
      setForm((prev) => ({ ...prev, ToDepartmentID: "" }));
      setError(err.message || "Failed to fetch complaint details");
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSupportingDocChange = async (e) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      setForm((prev) => ({ ...prev, SupportingDoc: null }));
      setSelectedDocInfo("");
      return;
    }

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      setError("Only JPG, JPEG, PNG files are allowed.");
      setForm((prev) => ({ ...prev, SupportingDoc: null }));
      setSelectedDocInfo("");
      e.target.value = "";
      return;
    }

    try {
      setError("");
      const processed = await compressImageToMaxSize(file, MAX_SUPPORTING_DOC_BYTES);
      if (processed.size > MAX_SUPPORTING_DOC_BYTES) {
        throw new Error("Image is too large even after compression. Please choose a smaller image.");
      }

      setForm((prev) => ({ ...prev, SupportingDoc: processed }));
      setSelectedDocInfo(`${processed.name} (${Math.ceil(processed.size / 1024)} KB)`);
    } catch (err) {
      setForm((prev) => ({ ...prev, SupportingDoc: null }));
      setSelectedDocInfo("");
      setError(err.message || "Failed to process image.");
    } finally {
      e.target.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!NEW_ALLOWED_MODULES.includes(moduleId)) {
      setError("Unauthorized access Or Session Timed out.");
      return;
    }

    if (!form.ComplaintCategoryID || !form.ComplaintID || !form.ResiTypeID || !form.ResidenceID || !form.Description || !form.ToDepartmentID) {
      setError("Required fields are missing.");
      return;
    }

    if (form.Description.length > 150) {
      setError("Brief Complaint Description/Remarks cannot exceed 150 characters.");
      return;
    }

    try {
      setLoading(true);

      const payload = new FormData();
      payload.append("moduleId", String(moduleId));
      payload.append("employeeId", String(employeeId));
      payload.append("ComplaintCategoryID", String(form.ComplaintCategoryID));
      payload.append("ComplaintID", String(form.ComplaintID));
      payload.append("ToDepartmentID", String(form.ToDepartmentID));
      payload.append("ResiTypeID", String(form.ResiTypeID));
      payload.append("ResidenceID", String(form.ResidenceID));
      payload.append("Description", String(form.Description));

      if (form.SupportingDoc) {
        payload.append("SupportingDoc", form.SupportingDoc);
      }

      const response = await fetch(`${API_BASE}/complaints/residential`, {
        method: "POST",
        body: payload
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Failed to submit complaint");
      }

      setSuccess(result?.message || "Your Complaint request is submitted successfully.");
      navigate("/residential-complaints", { replace: true });
    } catch (err) {
      setError(err.message || "Failed to submit complaint");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="container-fluid">
      <div style={{ textAlign: "left", marginTop: "10px" }}>
        <Link className="btn btn-primary" to="/residential-complaints">
          Complaint Status
        </Link>
      </div>

      <h3 className="mt-3 mb-3">New Complaint (Residential Only)</h3>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={onSubmit} className="row g-3">
        <input type="hidden" name="ToDepartmentID" value={form.ToDepartmentID} />

        <div className="col-sm-4">
          <label htmlFor="ComplaintCategoryID" className="form-label">Complaint Category</label>
          <select
            className="form-control"
            name="ComplaintCategoryID"
            id="ComplaintCategoryID"
            value={form.ComplaintCategoryID}
            onChange={(e) => onCategoryChange(e.target.value)}
            required
          >
            <option value="">Select Category</option>
            {categories.map((row) => (
              <option key={row.ComplaintCategoryID} value={row.ComplaintCategoryID}>
                {row.ComplaintCategory}
              </option>
            ))}
          </select>
        </div>

        <div className="col-sm-8">
          <label htmlFor="ComplaintID" className="form-label">Complaint Description</label>
          <select
            className="form-control"
            name="ComplaintID"
            id="ComplaintID"
            value={form.ComplaintID}
            onChange={onChange}
            required
          >
            <option value="">Select Complaint Description</option>
            {complaints.map((row) => (
              <option key={row.ComplaintID} value={row.ComplaintID}>
                {row.ComplaintDescription}
              </option>
            ))}
          </select>
        </div>

        <div className="col-sm-2">
          <label htmlFor="ResiTypeID" className="form-label">Area Type</label>
          <select
            className="form-control"
            name="ResiTypeID"
            id="ResiTypeID"
            value={form.ResiTypeID}
            onChange={onChange}
            required
          >
            <option value="">Select Area Type</option>
            {resiTypes.map((row) => (
              <option key={row.ResiTypeID} value={row.ResiTypeID}>
                {row.ResiType}
              </option>
            ))}
          </select>
        </div>

        <div className="col-sm-2">
          <label htmlFor="ResidenceID" className="form-label">House No.</label>
          <input
            className="form-control"
            type="text"
            name="ResidenceID"
            id="ResidenceID"
            value={form.ResidenceID}
            onChange={onChange}
            placeholder="HNo...*"
            required
          />
        </div>

        <div className="col-sm-8">
          <label htmlFor="Description" className="form-label">Brief Complaint Description/Remarks (Max 150 Characters)</label>
          <input
            className="form-control"
            type="text"
            name="Description"
            id="Description"
            value={form.Description}
            onChange={onChange}
            maxLength={150}
            placeholder="Complaint Description...*"
            required
          />
        </div>

        <div className="col-sm-6">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <label className="form-label mb-0">Supporting Image (JPG/JPEG/PNG, max 200KB)</label>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => uploadInputRef.current?.click()}
              title="Upload a JPG, JPEG, or PNG image. The image will be automatically resized to stay within 200 KB. On supported devices, you can also capture the image directly from the camera."
              aria-label="Upload a supporting image. The image will be automatically resized to stay within 200 KB, and on supported devices it can also be captured directly from the camera."
            >
              Upload
            </button>
            {selectedDocInfo && <span className="small text-muted">{selectedDocInfo}</span>}
          </div>
          <input
            ref={uploadInputRef}
            type="file"
            name="SupportingDoc"
            className="d-none"
            accept="image/jpeg,image/jpg,image/png"
            capture="environment"
            onChange={onSupportingDocChange}
          />
        </div>

        <div className="col-sm-12">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>
    </section>
  );
}
